/**
 * Environment-neutral QuoteEngine v1 contracts.
 *
 * This module deliberately contains no browser, persistence, React, or AI
 * implementation details. Adapters may translate these values at the edge;
 * the engine core only accepts JSON snapshots and content-addressed receipts.
 */

export type JsonPrimitive = string | number | boolean | null;
export type JsonValue = JsonPrimitive | JsonObject | JsonValue[];
export interface JsonObject {
    [key: string]: JsonValue;
}

export type Sha256Digest = `sha256:${string}`;

export const QUOTE_RUN_STATUSES_V1 = [
    'completed',
    'needs_human_review',
    'blocked',
    'failed',
    'cancelled',
    'unknown_outcome',
] as const;

export type QuoteRunStatusV1 = (typeof QUOTE_RUN_STATUSES_V1)[number];

export type StageDecisionOutcomeV1 = 'continue' | 'needs_human_review' | 'blocked';

export interface StageDecisionV1 {
    outcome: StageDecisionOutcomeV1;
    reasonCodes: string[];
    summary: string;
}

export interface StageInvariantV1 {
    key: string;
    status: 'passed' | 'failed';
    detail: string;
}

export interface StageTimingV1 {
    startedAt: string;
    completedAt: string;
    durationMs: number;
}

/**
 * Hash-chain receipt for one invocation. Timing is intentionally absent: a
 * rerun over identical snapshots must have the same receipt regardless of
 * wall-clock scheduling.
 */
export interface StageReceiptV1 {
    schemaVersion: 1;
    runId: string;
    stageKey: string;
    sequence: number;
    moduleVersion: string;
    attempt: number;
    invocationId: string;
    outcome: QuoteRunStatusV1;
    inputHash: Sha256Digest;
    outputHash: Sha256Digest;
    configurationHash: Sha256Digest;
    previousReceiptHash: Sha256Digest | null;
    receiptHash: Sha256Digest;
}

export interface StageResultV1<
    TOutput extends JsonValue = JsonValue,
    TPatch extends JsonObject = JsonObject,
> {
    schemaVersion: 1;
    stageKey: string;
    moduleVersion: string;
    status: QuoteRunStatusV1;
    inputHash: Sha256Digest;
    outputHash: Sha256Digest;
    configurationHash: Sha256Digest;
    output: TOutput;
    reducerPatch: TPatch;
    decision: StageDecisionV1;
    warnings: string[];
    invariants: StageInvariantV1[];
    aiReceiptRefs: string[];
    timing: StageTimingV1;
    attempt: number;
    receipt: StageReceiptV1;
}

export interface QuoteRunReceiptV1 {
    schemaVersion: 1;
    runId: string;
    projectId: string;
    status: QuoteRunStatusV1;
    requestHash: Sha256Digest;
    inputSnapshotHash: Sha256Digest;
    configurationSnapshotHash: Sha256Digest;
    projectVersionBefore: string;
    projectVersionAfter: string;
    quoteHash: Sha256Digest;
    stageReceiptHashes: Sha256Digest[];
    receiptHash: Sha256Digest;
}

export interface QuoteRunResultV1<TQuote extends JsonValue = JsonValue> {
    schemaVersion: 1;
    runId: string;
    projectId: string;
    status: QuoteRunStatusV1;
    inputSnapshotHash: Sha256Digest;
    configurationSnapshotHash: Sha256Digest;
    projectVersionBefore: string;
    projectVersionAfter: string;
    quote: TQuote;
    stages: StageResultV1[];
    receipt: QuoteRunReceiptV1;
}

export interface QuoteRunRequestV1 {
    schemaVersion: 1;
    runId: string;
    projectId: string;
    projectVersionBefore: string;
    inputSnapshot: JsonObject;
    configurationSnapshot: JsonObject;
}

export interface QuoteStageContextV1<TState extends JsonObject = JsonObject> {
    runId: string;
    projectId: string;
    projectVersion: string;
    stageKey: string;
    attempt: number;
    /** Engine-owned cancellation boundary for this exact stage invocation. */
    signal: AbortSignal;
    state: TState;
    configuration: JsonObject;
    priorStages: readonly StageResultV1[];
}

export interface QuoteStageExecutionV1<
    TOutput extends JsonValue = JsonValue,
    TPatch extends JsonObject = JsonObject,
> {
    output: TOutput;
    reducerPatch: TPatch;
    decision: StageDecisionV1;
    warnings: string[];
    invariants: StageInvariantV1[];
    aiReceiptRefs: string[];
    projectVersionAfter: string;
}

export interface QuoteStageModuleV1<
    TOutput extends JsonValue = JsonValue,
    TPatch extends JsonObject = JsonObject,
> {
    readonly key: string;
    readonly moduleVersion: string;
    execute(context: QuoteStageContextV1): Promise<QuoteStageExecutionV1<TOutput, TPatch>>;
}

/** The complete public surface. Persistence and UI adapters sit behind it. */
export interface QuoteEngine {
    /** Start once; duplicate callers observe the durable owner and its result. */
    startQuoteRun(request: QuoteRunRequestV1): Promise<QuoteRunResultV1>;
    /**
     * Explicit crash-recovery signal. If an invocation was reserved but has no
     * receipt, resume records unknown_outcome without invoking it again.
     */
    resumeQuoteRun(runId: string): Promise<QuoteRunResultV1 | null>;
    getQuoteRun(runId: string): Promise<QuoteRunResultV1 | null>;
    cancelQuoteRun(runId: string, reason: string): Promise<QuoteRunResultV1 | null>;
}

export class QuoteContractError extends Error {
    readonly path: string;

    constructor(path: string, message: string) {
        super(`${path}: ${message}`);
        this.name = 'QuoteContractError';
        this.path = path;
    }
}

export class UnknownStageOutcomeError extends Error {
    readonly code: string;

    constructor(message: string, code = 'unknown_stage_outcome') {
        super(message);
        this.name = 'UnknownStageOutcomeError';
        this.code = code;
    }
}

function fail(path: string, message: string): never {
    throw new QuoteContractError(path, message);
}

function expectRecord(value: unknown, path: string): Record<string, unknown> {
    if (value === null || typeof value !== 'object' || Array.isArray(value)) {
        fail(path, 'expected an object');
    }
    const prototype = Object.getPrototypeOf(value);
    if (prototype !== Object.prototype && prototype !== null) {
        fail(path, 'expected a plain object');
    }
    return value as Record<string, unknown>;
}

function expectString(value: unknown, path: string, allowEmpty = false): string {
    if (typeof value !== 'string' || (!allowEmpty && value.trim().length === 0)) {
        fail(path, allowEmpty ? 'expected a string' : 'expected a non-empty string');
    }
    return value;
}

function expectInteger(value: unknown, path: string, minimum: number): number {
    if (!Number.isInteger(value) || (value as number) < minimum) {
        fail(path, `expected an integer >= ${minimum}`);
    }
    return value as number;
}

function expectStringArray(value: unknown, path: string): string[] {
    if (!Array.isArray(value)) fail(path, 'expected an array');
    return value.map((entry, index) => expectString(entry, `${path}[${index}]`, true));
}

function expectDigest(value: unknown, path: string): Sha256Digest {
    const digest = expectString(value, path);
    if (!/^sha256:[a-f0-9]{64}$/.test(digest)) {
        fail(path, 'expected a lowercase sha256 digest');
    }
    return digest as Sha256Digest;
}

function expectStatus(value: unknown, path: string): QuoteRunStatusV1 {
    if (!QUOTE_RUN_STATUSES_V1.includes(value as QuoteRunStatusV1)) {
        fail(path, `expected one of ${QUOTE_RUN_STATUSES_V1.join(', ')}`);
    }
    return value as QuoteRunStatusV1;
}

function expectSchemaVersion(value: unknown, path: string): 1 {
    if (value !== 1) fail(path, 'expected schemaVersion 1');
    return 1;
}

export function assertJsonValue(value: unknown, path = '$', seen = new Set<object>()): asserts value is JsonValue {
    if (value === null || typeof value === 'string' || typeof value === 'boolean') return;
    if (typeof value === 'number') {
        if (!Number.isFinite(value)) fail(path, 'expected a finite JSON number');
        return;
    }
    if (typeof value !== 'object') fail(path, 'expected a JSON value');
    if (seen.has(value as object)) fail(path, 'cyclic values are not JSON');
    seen.add(value as object);
    if (Array.isArray(value)) {
        for (const key of Reflect.ownKeys(value)) {
            if (key === 'length') continue;
            if (typeof key === 'symbol') fail(path, 'symbol-keyed array properties are not JSON');
            const index = Number(key);
            if (!Number.isInteger(index) || index < 0 || String(index) !== key) {
                fail(`${path}.${key}`, 'custom array properties are not JSON');
            }
        }
        for (let index = 0; index < value.length; index += 1) {
            if (!Object.prototype.hasOwnProperty.call(value, index)) {
                fail(`${path}[${index}]`, 'sparse array entries are not JSON values');
            }
            assertJsonValue(value[index], `${path}[${index}]`, seen);
        }
    } else {
        const record = expectRecord(value, path);
        for (const key of Reflect.ownKeys(record)) {
            if (typeof key === 'symbol') fail(path, 'symbol-keyed properties are not JSON');
            if (!Object.prototype.propertyIsEnumerable.call(record, key)) {
                fail(`${path}.${key}`, 'non-enumerable properties are not JSON');
            }
        }
        for (const [key, entry] of Object.entries(record)) {
            assertJsonValue(entry, `${path}.${key}`, seen);
        }
    }
    seen.delete(value as object);
}

export function assertJsonObject(value: unknown, path = '$'): asserts value is JsonObject {
    assertJsonValue(value, path);
    if (value === null || typeof value !== 'object' || Array.isArray(value)) {
        fail(path, 'expected a JSON object');
    }
}

export function assertQuoteRunRequestV1(value: unknown): asserts value is QuoteRunRequestV1 {
    const request = expectRecord(value, '$request');
    expectSchemaVersion(request.schemaVersion, '$request.schemaVersion');
    expectString(request.runId, '$request.runId');
    expectString(request.projectId, '$request.projectId');
    expectString(request.projectVersionBefore, '$request.projectVersionBefore');
    assertJsonObject(request.inputSnapshot, '$request.inputSnapshot');
    assertJsonObject(request.configurationSnapshot, '$request.configurationSnapshot');
}

function assertStageDecisionV1(value: unknown, path: string): asserts value is StageDecisionV1 {
    const decision = expectRecord(value, path);
    if (!['continue', 'needs_human_review', 'blocked'].includes(String(decision.outcome))) {
        fail(`${path}.outcome`, 'expected continue, needs_human_review, or blocked');
    }
    expectStringArray(decision.reasonCodes, `${path}.reasonCodes`);
    expectString(decision.summary, `${path}.summary`, true);
}

function assertStageInvariantV1(value: unknown, path: string): asserts value is StageInvariantV1 {
    const invariant = expectRecord(value, path);
    expectString(invariant.key, `${path}.key`);
    if (invariant.status !== 'passed' && invariant.status !== 'failed') {
        fail(`${path}.status`, 'expected passed or failed');
    }
    expectString(invariant.detail, `${path}.detail`, true);
}

function assertStageTimingV1(value: unknown, path: string): asserts value is StageTimingV1 {
    const timing = expectRecord(value, path);
    expectString(timing.startedAt, `${path}.startedAt`);
    expectString(timing.completedAt, `${path}.completedAt`);
    if (typeof timing.durationMs !== 'number' || !Number.isFinite(timing.durationMs) || timing.durationMs < 0) {
        fail(`${path}.durationMs`, 'expected a finite number >= 0');
    }
}

export function assertStageReceiptV1(value: unknown, path = '$receipt'): asserts value is StageReceiptV1 {
    const receipt = expectRecord(value, path);
    expectSchemaVersion(receipt.schemaVersion, `${path}.schemaVersion`);
    expectString(receipt.runId, `${path}.runId`);
    expectString(receipt.stageKey, `${path}.stageKey`);
    expectInteger(receipt.sequence, `${path}.sequence`, 0);
    expectString(receipt.moduleVersion, `${path}.moduleVersion`);
    expectInteger(receipt.attempt, `${path}.attempt`, 1);
    expectString(receipt.invocationId, `${path}.invocationId`);
    expectStatus(receipt.outcome, `${path}.outcome`);
    expectDigest(receipt.inputHash, `${path}.inputHash`);
    expectDigest(receipt.outputHash, `${path}.outputHash`);
    expectDigest(receipt.configurationHash, `${path}.configurationHash`);
    if (receipt.previousReceiptHash !== null) {
        expectDigest(receipt.previousReceiptHash, `${path}.previousReceiptHash`);
    }
    expectDigest(receipt.receiptHash, `${path}.receiptHash`);
}

export function assertStageResultV1(value: unknown, path = '$stage'): asserts value is StageResultV1 {
    const stage = expectRecord(value, path);
    expectSchemaVersion(stage.schemaVersion, `${path}.schemaVersion`);
    expectString(stage.stageKey, `${path}.stageKey`);
    expectString(stage.moduleVersion, `${path}.moduleVersion`);
    expectStatus(stage.status, `${path}.status`);
    expectDigest(stage.inputHash, `${path}.inputHash`);
    expectDigest(stage.outputHash, `${path}.outputHash`);
    expectDigest(stage.configurationHash, `${path}.configurationHash`);
    assertJsonValue(stage.output, `${path}.output`);
    assertJsonObject(stage.reducerPatch, `${path}.reducerPatch`);
    assertStageDecisionV1(stage.decision, `${path}.decision`);
    expectStringArray(stage.warnings, `${path}.warnings`);
    if (!Array.isArray(stage.invariants)) fail(`${path}.invariants`, 'expected an array');
    stage.invariants.forEach((entry, index) => assertStageInvariantV1(entry, `${path}.invariants[${index}]`));
    expectStringArray(stage.aiReceiptRefs, `${path}.aiReceiptRefs`);
    assertStageTimingV1(stage.timing, `${path}.timing`);
    expectInteger(stage.attempt, `${path}.attempt`, 1);
    assertStageReceiptV1(stage.receipt, `${path}.receipt`);
}

export function assertQuoteRunReceiptV1(value: unknown, path = '$receipt'): asserts value is QuoteRunReceiptV1 {
    const receipt = expectRecord(value, path);
    expectSchemaVersion(receipt.schemaVersion, `${path}.schemaVersion`);
    expectString(receipt.runId, `${path}.runId`);
    expectString(receipt.projectId, `${path}.projectId`);
    expectStatus(receipt.status, `${path}.status`);
    expectDigest(receipt.requestHash, `${path}.requestHash`);
    expectDigest(receipt.inputSnapshotHash, `${path}.inputSnapshotHash`);
    expectDigest(receipt.configurationSnapshotHash, `${path}.configurationSnapshotHash`);
    expectString(receipt.projectVersionBefore, `${path}.projectVersionBefore`);
    expectString(receipt.projectVersionAfter, `${path}.projectVersionAfter`);
    expectDigest(receipt.quoteHash, `${path}.quoteHash`);
    if (!Array.isArray(receipt.stageReceiptHashes)) {
        fail(`${path}.stageReceiptHashes`, 'expected an array');
    }
    receipt.stageReceiptHashes.forEach((entry, index) => expectDigest(entry, `${path}.stageReceiptHashes[${index}]`));
    expectDigest(receipt.receiptHash, `${path}.receiptHash`);
}

export function assertQuoteRunResultV1(value: unknown): asserts value is QuoteRunResultV1 {
    const result = expectRecord(value, '$result');
    expectSchemaVersion(result.schemaVersion, '$result.schemaVersion');
    expectString(result.runId, '$result.runId');
    expectString(result.projectId, '$result.projectId');
    expectStatus(result.status, '$result.status');
    expectDigest(result.inputSnapshotHash, '$result.inputSnapshotHash');
    expectDigest(result.configurationSnapshotHash, '$result.configurationSnapshotHash');
    expectString(result.projectVersionBefore, '$result.projectVersionBefore');
    expectString(result.projectVersionAfter, '$result.projectVersionAfter');
    assertJsonValue(result.quote, '$result.quote');
    if (!Array.isArray(result.stages)) fail('$result.stages', 'expected an array');
    result.stages.forEach((entry, index) => assertStageResultV1(entry, `$result.stages[${index}]`));
    assertQuoteRunReceiptV1(result.receipt, '$result.receipt');
}

export function assertQuoteStageExecutionV1(value: unknown, path = '$execution'): asserts value is QuoteStageExecutionV1 {
    const execution = expectRecord(value, path);
    assertJsonValue(execution.output, `${path}.output`);
    assertJsonObject(execution.reducerPatch, `${path}.reducerPatch`);
    assertStageDecisionV1(execution.decision, `${path}.decision`);
    expectStringArray(execution.warnings, `${path}.warnings`);
    if (!Array.isArray(execution.invariants)) fail(`${path}.invariants`, 'expected an array');
    execution.invariants.forEach((entry, index) => assertStageInvariantV1(entry, `${path}.invariants[${index}]`));
    expectStringArray(execution.aiReceiptRefs, `${path}.aiReceiptRefs`);
    expectString(execution.projectVersionAfter, `${path}.projectVersionAfter`);
}
